import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X, HelpCircle } from 'lucide-react';
import './Pricing.css';

const Pricing = () => {
  const toggleFeatureInfo = (e) => {
    e.preventDefault();
    alert('Feature info would appear in a tooltip in the complete implementation.');
  };
  
  return (
    <div className="pricing-container">
      <section className="page-header">
        <div className="container">
          <h1>Simple, Transparent Pricing</h1>
          <p>Choose the plan that fits your needs</p>
        </div>
      </section>
      
      <section className="pricing-plans">
        <div className="container">
          <div className="plan-toggle">
            <span className="toggle-label active">Monthly</span>
            <div className="toggle-switch">
              <div className="toggle-handle"></div>
            </div>
            <span className="toggle-label">Annual <span className="discount-badge">Save 20%</span></span>
          </div>
          
          <div className="plans-grid">
            <div className="plan-card">
              <div className="plan-header">
                <h3>Starter</h3>
                <div className="plan-price">
                  <span className="price">$29</span>
                  <span className="period">/month</span>
                </div>
                <p className="plan-description">Perfect for individual photographers and small events</p>
              </div>
              
              <div className="plan-features">
                <ul>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>500 image uploads per month</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>5 GB storage</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>10 AI background removals</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>Basic templates</span>
                  </li>
                  <li className="feature-item">
                    <X size={18} className="feature-icon excluded" />
                    <span>Team collaboration</span>
                  </li>
                  <li className="feature-item">
                    <X size={18} className="feature-icon excluded" />
                    <span>API access</span>
                  </li>
                </ul>
              </div>
              
              <div className="plan-cta">
                <Link to="/register?plan=starter" className="plan-button">Get Started</Link>
              </div>
            </div>
            
            <div className="plan-card popular">
              <div className="popular-badge">Most Popular</div>
              <div className="plan-header">
                <h3>Professional</h3>
                <div className="plan-price">
                  <span className="price">$79</span>
                  <span className="period">/month</span>
                </div>
                <p className="plan-description">For professional photographers and sports organizations</p>
              </div>
              
              <div className="plan-features">
                <ul>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>2,000 image uploads per month</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>25 GB storage</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>50 AI background removals</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>Premium templates</span>
                    <button className="info-icon" onClick={toggleFeatureInfo}>
                      <HelpCircle size={14} />
                    </button>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>Team collaboration (up to 5 users)</span>
                  </li>
                  <li className="feature-item">
                    <X size={18} className="feature-icon excluded" />
                    <span>API access</span>
                  </li>
                </ul>
              </div>
              
              <div className="plan-cta">
                <Link to="/register?plan=professional" className="plan-button">Get Started</Link>
              </div>
            </div>
            
            <div className="plan-card">
              <div className="plan-header">
                <h3>Enterprise</h3>
                <div className="plan-price">
                  <span className="price">$199</span>
                  <span className="period">/month</span>
                </div>
                <p className="plan-description">For leagues, schools, and large organizations</p>
              </div>
              
              <div className="plan-features">
                <ul>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>Unlimited image uploads</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>100 GB storage</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>Unlimited AI background removals</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>Custom templates</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>Team collaboration (unlimited users)</span>
                  </li>
                  <li className="feature-item">
                    <Check size={18} className="feature-icon included" />
                    <span>API access</span>
                    <button className="info-icon" onClick={toggleFeatureInfo}>
                      <HelpCircle size={14} />
                    </button>
                  </li>
                </ul>
              </div>
              
              <div className="plan-cta">
                <Link to="/register?plan=enterprise" className="plan-button">Get Started</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="features-table">
        <div className="container">
          <h2 className="section-title">Compare Features</h2>
          <div className="table-container">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Starter</th>
                  <th>Professional</th>
                  <th>Enterprise</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Monthly uploads</td>
                  <td>500</td>
                  <td>2,000</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Storage</td>
                  <td>5 GB</td>
                  <td>25 GB</td>
                  <td>100 GB</td>
                </tr>
                <tr>
                  <td>AI background removals</td>
                  <td>10/month</td>
                  <td>50/month</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Team members</td>
                  <td>1</td>
                  <td>Up to 5</td>
                  <td>Unlimited</td>
                </tr>
                <tr>
                  <td>Templates</td>
                  <td>Basic</td>
                  <td>Premium</td>
                  <td>Custom</td>
                </tr>
                <tr>
                  <td>API access</td>
                  <td><X size={18} className="icon-no" /></td>
                  <td><X size={18} className="icon-no" /></td>
                  <td><Check size={18} className="icon-yes" /></td>
                </tr>
                <tr>
                  <td>Priority support</td>
                  <td><X size={18} className="icon-no" /></td>
                  <td><Check size={18} className="icon-yes" /></td>
                  <td><Check size={18} className="icon-yes" /></td>
                </tr>
                <tr>
                  <td>Custom branding</td>
                  <td><X size={18} className="icon-no" /></td>
                  <td><X size={18} className="icon-no" /></td>
                  <td><Check size={18} className="icon-yes" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
      
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Can I change plans later?</h3>
              <p>Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.</p>
            </div>
            
            <div className="faq-item">
              <h3>Is there a free trial?</h3>
              <p>We offer a 14-day free trial on all plans so you can test out the features before committing.</p>
            </div>
            
            <div className="faq-item">
              <h3>How does billing work?</h3>
              <p>We bill monthly or annually, depending on your preference. Annual plans include a 20% discount.</p>
            </div>
            
            <div className="faq-item">
              <h3>Can I cancel anytime?</h3>
              <p>Yes, you can cancel your subscription at any time with no penalties.</p>
            </div>
          </div>
        </div>
      </section>
      
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to get started?</h2>
            <p>Join thousands of sports photographers who have elevated their game with PlayMaker Pics.</p>
            <Link to="/register" className="cta-button">Start Your Free Trial</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing; 